import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ExternalLink, Droplets, TreePine, Snowflake, Fish, Landmark, Home as HomeIcon, Mountain, Waves, Tent, Zap, Hammer, Sparkles } from "lucide-react";
import { StyvmorsviolOrnament } from "@/components/Ornaments";

const STORIES = {
  "mystiska-korset": {
    icon: Sparkles,
    title: "Mystiska korset",
    subtitle: "En 1 000–2 000 år gammal ockramålning på en jättesten i Jansjöskogen",
    content: [
      "På toppen av Djuptjärnsstigen, där stigen kröker av mot Jansjöbodarnas gamla fäbodvall, vilar ett flyttblock så stort att det ser ut att ha blivit placerat där av en hand som inte är vår. På blockets släta sydsida finns en målning. Ett kors. Men inte ett kristet kors – det här är äldre än så.",
      "Arkeologer daterar målningen till någonstans mellan år 0 och år 1000 efter Kristus. Tekniken är enkel och igenkännbar: rödockra bränd och slammad, blandad med fett och kanske äggvita, ströken på stenen med fingrar eller med en fläng av näver. Samma metod som hällmålarna använde redan för 6 000 år sedan vid Brattforsberget, några mil härifrån.",
      "Men symbolen är gåtfull. Ett kors – varför? Ett vägmärke? En fångstmagi? En tack till ett bytes­djur eller en ande? Vi vet inte. Vi får aldrig veta. Och det är just det som gör platsen vacker.",
      "Stenen står i något som kallas ”istidsskogen” – ett område strödd med jätteflyttblock som lämnades efter av den inlandsis som låg över Norrland för 10 000 år sedan. Blocken är en egen skulptur­samling, utställd av tiden själv. Mellan dem växer pelartallar och mossa så tjock att den fjädrar under stegen.",
      "Att komma hit kräver lite: Djuptjärnsstigen från Jansjö är 10,5 km tur och retur, men avstickaren till Mystiska korset är bara några hundra meter från huvudleden. Skylt finns vid avtaget. Gå tyst. Var inte i flock. Lägg handen på stenen, men inte på målningen. Rödockran är sårbar.",
      "Fjällsjö är en av de glesast befolkade delarna av Sverige. Men de som gick här före oss lämnade spår. Stenen väntar.",
    ],
    sources: [
      { text: "Djuptjärnsstigen – backe.nu/vandringseldorado", url: "https://www.backe.nu/sv/vandringseldorado" },
      { text: "Hällmålningar i Ångermanland – Länsstyrelsen", url: "https://www.lansstyrelsen.se/vasternorrland" },
    ],
    next: "historien",
  },
  "vattnet": {
    icon: Droplets,
    title: "Vattnet",
    subtitle: "18 mil stränder, 69 sjöar och 3 mil strömmande vatten",
    content: [
      "Rossöns fiskevårdsområde omfattar 69 sjöar, 18 mil stränder och 3 mil strömmande vatten. Det är ett av de mest vattenrika områdena i hela Ångermanland. Här finns abborre, harr, gädda, sik och öring – i vatten som aldrig har luktat av industri.",
      "Fjällsjöälven rinner genom hela bygden, från Rossön ner genom Backe. Den är inte bred och den är inte vild. Men den bär. Den har burit flottningsvirke, den har gett fisk åt generationer, och den ger fortfarande barnen i Backe sin badplats vid Stenhammaren – med bryggor, hopptorn och en strand som värms av eftermiddagssolen.",
      "Fiskekort för hela Rossöns FVO köper man enklast via iFiske.se eller på byservicekontoret i Backe. Ett säsongskort kostar runt 250 kronor och ger tillgång till samtliga 69 sjöar. Flugfisket i Vängelälven är särskilt uppskattat bland dem som söker harr och öring.",
      "Vid Sunnansjögården utanför Backe erbjuds björnskådning från fotogömsle – en av få platser i Ångermanland där man kan se björn på nära håll, guidat och tryggt.",
    ],
    sources: [
      { text: "Rossöns FVO – fiskekort och sjöar", url: "https://rossonfvo.se" },
      { text: "Badplats Stenhammaren – backe.nu", url: "http://backe.nu/sv/att-gora" },
      { text: "Sunnansjögården björnskådning", url: "http://backe.nu/sv/article/sunnansjogarden-jakt-fiske" },
    ],
    next: "skogen",
  },
  "skogen": {
    icon: TreePine,
    title: "Skogen",
    subtitle: "6 mil vandringsleder genom Fjällsjö Vandringseldorado",
    content: [
      "Fjällsjö Vandringseldorado är ett nätverk av vandringsleder som sträcker sig 6 mil genom bygden – från Backe via Vängel och vidare mot fjällen. Lederna underhålls av lokala föreningar och passerar genom gammal tallskog, längs älvar och över berg med utsikt man inte kan googla sig till.",
      "Bollsbergets naturreservat ligger 3–4 kilometer väster om Rossön. Här reser sig dramatiska berg mellan 215 och 379 meter över havet, klädda i gammal tallskog som aldrig har kalavverkats. I dalarna gömmer sig små sjöar, och på de branta sluttningarna trivs rödlistade arter som spår av en skog som fått vara ifred.",
      "Vid Brattforsberget, Brinnåsklippen och Nagasjö finns hällmålningar som är 6 000 år gamla. Stenålderns jägare målade älgar, zigzag-mönster och processioner med rödockra på klippväggarna. GPS-koordinater till målningarna finns publicerade på backe.nu.",
      "Skogen här är inte kuliss. Den är arbetsplats, skafferi och fristad. Folk plockar bär på hösten, jagar älg i oktober och går på svamp när fukten kommer. Man behöver inte vara friluftsexpert – bara villig att gå ut genom dörren.",
    ],
    sources: [
      { text: "Vandringsleder – backe.nu (stigbeskrivningar)", url: "http://backe.nu/sites/default/files/fileuploads/stigbeskrivningar.pdf" },
      { text: "Bollsbergets naturreservat – Länsstyrelsen Jämtland", url: "https://www.lansstyrelsen.se/jamtland" },
      { text: "Hällmålningar – backe.nu/kultur", url: "http://backe.nu/sv/kultur" },
    ],
    next: "vintern",
  },
  "vintern": {
    icon: Snowflake,
    title: "Vintern",
    subtitle: "Elljusspår, slalom och skoterleder i snötäckt skog",
    content: [
      "Vintern i Fjällsjö och Bodum börjar i november och släpper inte taget förrän i april. Det är mörkt, det är kallt, och det är precis därför elljusspåren finns.",
      "I Backe lyser elljusspåret vid skidstugan 2,2 kilometer genom skogen, med belysning till klockan 21. I Rossön har Rossöns IF tre slingor: 0,6 km, 1,8 km och 2,6 km plus en obelyst 5 km-slinga för den som vill ha mer. Spåren prepareras av ideella krafter – samma folk som ställer upp med allt annat i bygden.",
      "Landsomberget i Backe är slalombacken – inte Åre, inte Sälen, men en riktig backe med lift, som drivs av engagemang och frivillighet. Barnen lär sig åka här, och vuxna kommer hit för att det är nära, billigt och roligt.",
      "Skoterlederna sträcker sig genom hela området och kopplar ihop bygderna med fjällen i väster. Strömsunds kommun underhåller huvudlederna, men lokalkännedom är det bästa GPS:et.",
    ],
    sources: [
      { text: "Elljusspår Backe – backe.nu", url: "http://backe.nu/sv/article/skidspar-langs-alven-och-elljussparet" },
      { text: "Elljusspår Rossön – stromsund.se", url: "https://www.stromsund.se/966.html" },
      { text: "Landsomberget – stromsund.se", url: "https://www.stromsund.se/1667.html" },
    ],
    next: "sjoarna",
  },
  "sjoarna": {
    icon: Fish,
    title: "Sjöarna",
    subtitle: "69 sjöar – Ångermanlands bäst bevarade hemlighet",
    content: [
      "Rossöns fiskevårdsområde är unikt i sitt slag. 69 namngivna sjöar, spridda genom en terräng av morän, myr och skog. I många av dem simmar abborre, gädda och sik sida vid sida med harr och öring – arter som kräver rent, syrerikt vatten.",
      "Vängelälven, som rinner genom landskapet väster om Backe, är känd bland flugfiskare för sin harr. Älven är inte stor, men den är rik – och den som har tålamod belönas oftare än inte.",
      "Fiskekort för hela FVO-området kostar runt 250 kronor för ett säsongskort. Det köps enklast via iFiske.se, på ICA i Rossön eller på byservicekontoret i Backe. Ett dagskort kostar 50 kronor.",
      "Vid Sunnansjögården utanför Backe kan man boka guidat fiske och björnskådning. Gömslet ligger vid en utfodringsplats och ger möjlighet att se björn på bara några meters avstånd. Det är inte turistattraktionen som skriker – det är den som viskar.",
    ],
    sources: [
      { text: "Rossöns FVO – sjöar och fiskekort", url: "https://rossonfvo.se" },
      { text: "Sunnansjögården jakt & fiske", url: "http://backe.nu/sv/article/sunnansjogarden-jakt-fiske" },
      { text: "Fiske i Strömsunds kommun – stromsund.se", url: "https://www.stromsund.se/3383.html" },
    ],
    next: "historien",
  },
  "historien": {
    icon: Landmark,
    title: "Historien",
    subtitle: "6 000 år av mänsklig närvaro vid Fjällsjöälven",
    content: [
      "Det är lätt att tro att historien i Norrland började med industrialiseringen. Men i Fjällsjö och Bodum har människor bott i minst sex tusen år. Arkeologerna har dokumenterat över 120 stenåldersboplatser i området – ett av de tätaste koncentrationerna i Ångermanland.",
      "Rossöns Stenåldersmuseum på Öhnvägen 5 i Rossön samlar fynd från dessa boplatser: verktyg, pilspetsar, skrapor av kvarts och skiffer. Museet drivs ideellt och är öppet på sommaren eller efter överenskommelse. Här kan man också prova stenåldersbowling – en aktivitet som låter absurd tills man har försökt.",
      "Hällmålningarna vid Brattforsberget visar älgfigurer, målade med rödockra direkt på berget. Vid Brinnåsklippen finns älgar och zigzag-mönster. Vid Nagasjö en procession av figurer. Och vid Mystiska Korset – ett tecken som ingen har kunnat tyda. Alla är daterade till ungefär 4 000 f.Kr.",
      "Den som vandrar här vandrar bokstavligen i stenålderns fotspår. Samma stigar, samma vatten, samma berg. Landskapet har inte förändrats. Bara vi som bor i det.",
    ],
    sources: [
      { text: "Rossöns Stenåldersmuseum", url: "https://www.stromsund.se/3642.html" },
      { text: "Hällmålningar – backe.nu/kultur", url: "http://backe.nu/sv/kultur" },
      { text: "Kulturhistoria Strömsunds kommun", url: "https://www.stromsund.se/3642.html" },
    ],
    next: "bygden",
  },
  "bygden": {
    icon: HomeIcon,
    title: "Bygden",
    subtitle: "Fjällsjömarknaden, Tunnbrödets dag och musikkafé",
    content: [
      "Fjällsjömarknaden fyller 80 år sommaren 2026 och firas i juli med något som inte finns på många andra marknader i Sverige: Sverigecupen i Watercross. Snöskotrar som kör på vatten. Det är precis så galet som det låter, och precis därför kommer folk.",
      "Bodums hembygdsförening i Rossön håller traditionen vid liv med Tunnbrödets dag – en dag då man bakar tunnbröd i vedugn på hembygdsgården vid Hällnäset, precis som man har gjort i generationer. Midsommar firas på samma plats, med stång, dans och fiol.",
      "I Rossöns Folkets Hus arrangeras musikkafé med levande musik. Höstmarknaden med loppis samlar folk från hela kommunen. Backe IF arrangerar egna loppisar med hembakat fika – och den som aldrig har ätit en hembakad kanelbulle från Backe IF har missat något.",
      "Det som binder ihop bygden är inte evenemanget utan viljan. Samma människor som kör spårmaskinen på vintern bakar bullar på sommaren. Samma familjer som fixar fotbollsplanen organiserar marknaden. Det är inte ett jobb. Det är så det fungerar här.",
    ],
    sources: [
      { text: "Fjällsjömarknaden – stromsund.se", url: "https://www.stromsund.se/3365.html" },
      { text: "Bodums hembygdsförening", url: "https://www.hembygd.se/bodum" },
      { text: "Backe IF", url: "http://www.backeif.se" },
    ],
    next: "bollsberget",
  },
  "bollsberget": {
    icon: Mountain,
    title: "Bollsbergets naturreservat",
    subtitle: "Dramatiska berg och orörd skog 3 km väster om Rossön",
    content: [
      "Bollsbergets naturreservat ligger bara 3–4 kilometer väster om Rossön, men de flesta som kör förbi på väg 345 vet inte att det finns. Reservatet skyddades av Länsstyrelsen i Jämtlands län för sin unika natur – gammal tallskog som aldrig har kalavverkats, branta bergssidor och en biologisk mångfald som är ovanlig så här långt norrut.",
      "Bergen reser sig mellan 215 och 379 meter över havet. Sluttningarna är branta, och i dalarna gömmer sig små tjärnar omgivna av mossa och blockmark. Här trivs rödlistade arter – vedsvampar, lavar och fåglar som är beroende av gammal, orörd skog.",
      "Det finns inga markerade leder, inga parkeringsplatser med skyltar, inga bänkar. Det är poängen. Det här är en plats man hittar genom att fråga en Rossöbo – eller genom att bara gå in i skogen och se vad som händer.",
    ],
    sources: [
      { text: "Länsstyrelsen Jämtland – naturreservat", url: "https://www.lansstyrelsen.se/jamtland" },
    ],
    next: "hallmalningarna",
  },
  "hallmalningarna": {
    icon: Landmark,
    title: "Hällmålningarna",
    subtitle: "6 000 år gamla målningar vid Brattforsberget",
    content: [
      "För ungefär sex tusen år sedan stod en människa vid foten av Brattforsberget, blandade rödockra med fett och målade en älg på klippan. Vi vet inte varför. Vi vet inte vem. Men älgen finns kvar.",
      "Hällmålningarna i Fjällsjö är bland de nordligaste i Sverige. Vid Brattforsberget dominerar älgfigurer – målade med tydlig kontur i rött mot den grå stenytan. Vid Brinnåsklippen finns älgar och zigzag-mönster som kan tolkas som vatten eller berg. Vid Nagasjö visar målningarna en procession av figurer – kanske en jaktscen, kanske en ceremoni.",
      "Och så finns Mystiska Korset – en hällmålning som ingen forskare har kunnat förklara. Det är inte ett kristet kors. Det är äldre. Det är bara ett tecken, målat på en klippa i skogen, och det väntar fortfarande på sin förklaring.",
      "GPS-koordinater till samtliga hällmålningar finns publicerade på backe.nu. Man kan gå dit själv. Det är ingen inträde, inga öppettider, inget stängsel. Bara du, skogen och sex tusen år av tystnad.",
    ],
    sources: [
      { text: "Hällmålningar – backe.nu", url: "http://backe.nu/sv/kultur" },
      { text: "Strömsunds kommun kulturhistoria", url: "https://www.stromsund.se/3642.html" },
    ],
    next: "13-sjoar",
  },
  "13-sjoar": {
    icon: Droplets,
    title: "13 sjöar på 3,5 km",
    subtitle: "Väg 346 mellan Rossön och Backe",
    content: [
      "Kör väg 346 från Rossön mot Backe. På en enda sträcka av 3,5 kilometer passerar du 13 sjöar – de flesta synliga direkt från bilen. Det är inte en turistväg. Det är bara en vanlig landsväg i Ångermanland. Men den råkar passera genom ett av de mest sjörika landskapen i Sverige.",
      "En av sjöarna heter Gröna ögat. Den byter färg beroende på ljus och årstid – smaragdgrön i solsken, nästan svart i gråväder. Ingen skylt markerar den. Man stannar bara och tittar, om man vet att den finns.",
      "Det är den sortens plats som aldrig hamnar i en broschyr. Inte för att den inte är värd det, utan för att ingen tänker på att berätta. Det är bara vägen hem.",
    ],
    sources: [
      { text: "Väg 346 – elnorrland.wordpress.com", url: "https://elnorrland.wordpress.com" },
    ],
    next: "finlanda",
  },
  "finlanda": {
    icon: Mountain,
    title: "Finlanda flygfält",
    subtitle: "Gräsbana, två hangarer och vindstrut – 2,2 km nordväst om Rossön",
    content: [
      "Finlanda flygfält ligger 2,2 kilometer nordväst om Rossön. Det har en gräsbana på cirka 500 meter, två hangarer och en vindstrut. Det är inte stort, det är inte modernt, och det är inte känt. Men det fungerar.",
      "Fältet används av privatflygare och har betyg 3.6 på Flygkartan.se – ett av de mindre kända flygfälten i Jämtlands län. Det faktum att det finns ett aktivt flygfält i en by med 300 invånare säger något om platsen. Kanske att folk här gör saker – inte för att det finns en marknad för det, utan för att de kan.",
      "Hallvikens flygfält, strax norr om Strömsund, har en ännu märkligare historia. Det anlades som militärt beredskapsflygfält under andra världskriget och lades ner 1974. Strömsunds flygklubb håller det vid liv – en bit levande historia i skogen.",
    ],
    sources: [
      { text: "Finlanda flygfält – Flygkartan.se", url: "https://flygkartan.se" },
      { text: "Hallvikens flygfält – Wikipedia", url: "https://sv.wikipedia.org/wiki/Hallvikens_flygf%C3%A4lt" },
    ],
    next: "stenhammaren",
  },
  "stenhammaren": {
    icon: Tent,
    title: "Stenhammaren",
    subtitle: "Backes badplats och camping vid älven",
    content: [
      "Strax söder om Backe sluttar marken mjukt ner mot Fjällsjöälven. Här ligger Stenhammaren – bygdens badplats sedan generationer. Sanden är fin, vattnet är kallt in i juli men varmt i augusti, och bryggan sträcker sig ut över det lugna älvpartiet där strömmen saktar ner.",
      "Det finns ett hopptorn. Det finns en gräsmatta där folk grillar. På sommaren är campingen öppen – enkla stugor, ställplatser för husvagn, toalett och dusch. Inget lyxigt, men allt som behövs för några nätter vid älven.",
      "Det är hit familjer kommer på midsommarkvällen. Det är hit barnen cyklar efter skolan i juni. Det är hit besökare på väg norrut svänger av för att bada en timme innan de kör vidare mot fjällen.",
      "Drivs lokalt, öppen säsongsvis. Priset har aldrig varit anledningen att folk kommer tillbaka.",
    ],
    sources: [
      { text: "Badplats Stenhammaren – backe.nu", url: "http://backe.nu/sv/att-gora" },
      { text: "Camping i Strömsunds kommun", url: "https://www.stromsund.se/3383.html" },
    ],
    next: "landsomberget",
  },
  "landsomberget": {
    icon: Zap,
    title: "Landsomberget",
    subtitle: "Slalombacken som ingen utanför bygden vet om",
    content: [
      "Landsomberget är Backes slalombacke. Den har en lift, några belysta nedfarter och en stuga där barnen värmer sig med varm choklad efter åkturerna. Det är ingen Åre, inte ens Funäsdalen. Det är en liten backe i en liten bygd – och det är poängen.",
      "Här lär sig barnen från bygden åka skidor. Samma föräldrar som körde spårmaskinen på morgonen sitter i liftstugan på eftermiddagen och säljer kaffe. Ett dagskort kostar en bråkdel av vad det kostar på fjällanläggningarna, och det är för att ingen ska tjäna pengar på det. Det är för att det ska finnas.",
      "Backen drivs ideellt av föreningen som bakom kulisserna håller liftmotorn igång, gör snö när naturen inte räcker till, och skottar trappan till stugan efter varje snöfall.",
      "Öppnar när snön kommer och stänger när den försvinner. Öppettider publiceras löpande på backe.nu.",
    ],
    sources: [
      { text: "Landsomberget – stromsund.se", url: "https://www.stromsund.se/1667.html" },
      { text: "Backe.nu – idrott och friluftsliv", url: "http://backe.nu/sv/att-gora" },
    ],
    next: "stenaldersmuseum",
  },
  "stenaldersmuseum": {
    icon: Hammer,
    title: "Stenåldersmuseet",
    subtitle: "6 000 år under ett tak i Rossön",
    content: [
      "På Öhnvägen 5 i Rossön ligger ett museum som få turister hittar till. Rossöns Stenåldersmuseum drivs ideellt och rymmer fynd från över 120 dokumenterade stenåldersboplatser i området – verktyg, pilspetsar och skrapor i kvarts och skiffer, vissa så välformade att man förstår hur lång tid det måste ha tagit att slå till dem.",
      "Museet är inte stort. En samling i ett rum, prydligt uppställd, med texter som förklarar vad som hittats och var. Men det är unikt så här långt norrut. Fynden visar att människor bott och jagat här i minst sextusen år – långt innan nationsgränser, långt innan någon hette något på kartan.",
      "Här finns också stenåldersbowling – en aktivitet som låter absurd tills man provat. Man rullar klot av kvartsit mot resta trälappar. Det är enkelt, skrattretande och förvånansvärt svårt.",
      "Öppet på sommaren och efter överenskommelse resten av året. Bokning via Strömsunds kommun eller hembygdsföreningen.",
    ],
    sources: [
      { text: "Rossöns Stenåldersmuseum – stromsund.se", url: "https://www.stromsund.se/3642.html" },
      { text: "Kulturhistoria Strömsunds kommun", url: "https://www.stromsund.se/3642.html" },
    ],
    next: "namnet",
  },
  "namnet": {
    icon: Waves,
    title: "Varför vidalv.live?",
    subtitle: "Om älven, flottningen och varför sidan heter som den gör",
    content: [
      "Fjällsjöälven har alltid gjort jobbet.",
      "I över tre hundra år flottades timmer på den här älven. Stockar fälldes i skogen under vintern, kördes med häst ner till stränderna och staplades på isen. När våren kom och isen bröt upp började flottningen. Timret drev nerströms, mot sågarna och massabruken vid kusten. Flottarna gick med. De bodde i enkla kojor, jobbade i iskallt vatten, sov på hårda britsar. Lönen var dålig, arbetet farligt. Flera drunknade varje vår.",
      "Men det var älven som bar bygden ekonomiskt. Utan flottningen hade få haft råd att bo kvar.",
      "Sista stocken flottades nerför Fjällsjöälven 1965. Efter det blev det lastbilar och grusväg istället. Kraftverken kom, och med dem dammarna som gjorde flottning omöjlig. Älven fortsatte att rinna – men den slutade att arbeta på det gamla sättet.",
      "'Vid älv' är så folk alltid beskrivit var vi bor. Det står inte på någon skylt, men det är så det sagts i kök och på arbetsplatser: vi bor vid älven. Det är plats, riktning och hemkänsla i två ord. Därför heter sidan vidalv. Inget försök att låta märkvärdigt, inget påklistrat – bara det namn som låg närmast till hands.",
      "Och varför .live?",
      "Lokal information har en tendens att åldras fort. Telefonnummer byts, föreningar får nya ordföranden, företag flyttar eller läggs ner. En lista som stämde för tre år sen är ofta fel idag. Det är där sidor om bygder brukar gå sönder – de byggs en gång och lämnas sedan att långsamt bli oanvändbara.",
      ".live betyder att det som står här ska stämma idag. Inte i fjol, inte imorgon – idag. När något ändras så ändras det på sidan. När en förening läggs ner så försvinner den. När ett nytt företag öppnar så kommer det in. Inskickade anslag granskas och publiceras inom dygnet.",
      "Älvens bärarroll har lastbilarna och skogsmaskinerna tagit över. Men att bära informationen levande – det måste någon fortfarande göra för hand. Det är det vi gör.",
    ],
    sources: [
      { text: "Flottning på Fjällsjöälven – Wikipedia", url: "https://sv.wikipedia.org/wiki/Fj%C3%A4llsj%C3%B6%C3%A4lven" },
      { text: "Ångermanälvens flottning – Skogsstyrelsen", url: "https://www.skogsstyrelsen.se" },
      { text: "Om bygdens historia – backe.nu", url: "http://backe.nu/sv/kultur" },
    ],
    next: "vattnet",
  },
};

const ALL_SLUGS = Object.keys(STORIES);

export default function UpptackStory() {
  const { slug } = useParams();
  const story = STORIES[slug];

  useEffect(() => {
    if (story) document.title = `${story.title} – vidalv.live`;
    window.scrollTo(0, 0);
  }, [slug, story]);

  if (!story) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 text-center">
        <h1 className="font-heading text-3xl font-semibold text-ink mb-4">Sidan finns inte</h1>
        <Link to="/" className="text-falu hover:text-falu-hover no-underline font-semibold">Tillbaka till startsidan</Link>
      </div>
    );
  }

  const Icon = story.icon;
  const nextStory = STORIES[story.next];

  return (
    <div data-testid={`story-${slug}`} className="relative max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      {slug === "mystiska-korset" && (
        <>
          <StyvmorsviolOrnament className="hidden lg:block absolute top-12 -left-16 w-14 text-angermanland opacity-25" />
          <StyvmorsviolOrnament className="hidden lg:block absolute top-48 -right-16 w-14 text-angermanland opacity-20" />
        </>
      )}
      <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-semibold text-bark hover:text-falu no-underline transition-colors mb-8">
        <ArrowLeft size={14} /> Tillbaka till startsidan
      </Link>

      <div className="flex items-center gap-3 mb-3">
        <Icon size={22} className="text-pine" />
        <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em]">Upptäck bygden</p>
      </div>

      <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-ink tracking-tight mb-3">
        {story.title}
      </h1>
      <p className="text-base sm:text-lg text-bark/70 font-heading italic mb-10">
        {story.subtitle}
      </p>

      <div className="flex flex-col gap-5">
        {story.content.map((paragraph, i) => (
          <p key={i} className="text-sm sm:text-base text-ink/80 leading-relaxed font-body">
            {paragraph}
          </p>
        ))}
      </div>

      {/* Källor */}
      {story.sources && story.sources.length > 0 && (
        <div className="mt-10 pt-6 border-t border-warm-border">
          <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-3">Källor</p>
          <ul className="flex flex-col gap-2">
            {story.sources.map((s, i) => (
              <li key={i}>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-falu hover:text-falu-hover no-underline transition-colors">
                  <ExternalLink size={13} /> {s.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Next story */}
      {nextStory && (
        <div className="mt-10 pt-6 border-t border-warm-border">
          <Link
            to={`/upptack/${story.next}`}
            data-testid={`next-story-${story.next}`}
            className="flex items-center justify-between bg-parchment border border-warm-border rounded-sm p-5 hover:border-bark/40 transition-all no-underline group"
          >
            <div>
              <p className="font-body text-xs text-bark/50 mb-1">Läs vidare</p>
              <p className="font-heading text-xl font-semibold text-ink group-hover:text-falu transition-colors">{nextStory.title}</p>
              <p className="text-sm text-bark">{nextStory.subtitle}</p>
            </div>
            <ArrowRight size={20} className="text-bark/40 group-hover:text-falu transition-colors shrink-0" />
          </Link>
        </div>
      )}
    </div>
  );
}
